import isNil from 'lodash/isNil'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { useOrganisationSettingsByName } from '@omnea-app/fe-org-settings/useOrganisationSettings'
import { Price } from '@omnea-app/requests/price'
import { formatMonetaryAmount } from 'utils/currency/formatCurrency'
import { getPriceFrequencyLabel } from 'utils/price/getPriceFrequencyLabel'
import { getAmountInDefaultCurrency } from 'utils/price/getPriceInDefaultCurrency'
import { useOrganisationCurrencies } from '../currency/hooks/useOrganisationCurrencies'
import { getDefaultFxRate } from './getDefaultFxRate'

type PriceFormatterOptions = { hideFrequency?: boolean }

export function usePriceFormatter({ hideFrequency = false }: PriceFormatterOptions = {}) {
  const { organisationCurrenciesByCode: currenciesByCode } = useOrganisationCurrencies()
  const { organisationSetting: defaultCurrency } = useOrganisationSettingsByName('defaultCurrency')
  const { t } = useTranslation('requests')
  const { locale } = useFormatter()

  const formatPrice = useCallback(
    (price: Price) => {
      const frequencyLabel =
        price.paymentInterval != null && !hideFrequency
          ? getPriceFrequencyLabel({ paymentInterval: price.paymentInterval, multiYears: price.multiYears, t })
          : undefined
      const formattedPrice = `${formatMonetaryAmount(price.amount, price.currency, locale)}${!isNil(frequencyLabel) ? ` ${frequencyLabel}` : ''}`

      const defaultFxRate = getDefaultFxRate({ currenciesByCode, defaultCurrency: defaultCurrency?.defaultCurrency })

      const formattedDefaultPrice = !isNil(price.fxRate)
        ? `~${formatMonetaryAmount(price.amount / (defaultFxRate * price.fxRate), defaultCurrency?.defaultCurrency, locale)}`
        : undefined

      return {
        formattedPrice,
        formattedDefaultPrice,
      }
    },
    [hideFrequency, currenciesByCode, defaultCurrency, locale, t]
  )

  const getAmountInDefaultCurrencyFn = useCallback(
    (price: Price): number | undefined => {
      return getAmountInDefaultCurrency({ price, defaultCurrency: defaultCurrency?.defaultCurrency, currenciesByCode })
    },
    [defaultCurrency, currenciesByCode]
  )

  return {
    formatPrice,
    currenciesByCode,
    defaultCurrency: defaultCurrency?.defaultCurrency,
    getAmountInDefaultCurrency: getAmountInDefaultCurrencyFn,
  }
}
